import { useEffect, useContext, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { UIContext } from '../../contexts/UIContext';

import { useGetVectorFocus } from '../../hooks/useVectorFocusService';
import { useGetDenounceStatusForSelector } from '../../hooks/useDenounceStatusService';
import { useGetPossibleVectorFocusForSelector } from '../../hooks/usePossibleVectorFocusService';
import { useGetDiseaseVectorsForSelector } from '../../hooks/useDiseaseVectorsService';

import PicturesService from '../../services/PicturesService';
import VectorFocusService from '../../services/VectorFocusService';
import AddressService from '../../services/AddressService';

import { Paper, Button } from '@mui/material';
import Header from '../../components/Header/Header';
import BottomMenu from '../../components/BottomMenu/BottomMenu';
import CameraCapture from '../../components/CameraCapture/CameraCapture';
import UpdateVectorFocusForm from '../../components/Forms/UpdateVectorFocusForm/UpdateVectorFocusForm';
import Image from '../../components/Image/Image';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import AddressForm from '../../components/Forms/AddressForm/AddressForm';
import InformationDialogue from '../../components/InformationDialogue/InformationDialogue';

import './VectorFocusUpdate.scss';

function VectorFocusUpdate() {
  const { authUser, isAuthorized } = useContext(AuthContext);
  const {
    INFO_DIALOGUE_MSGS,
    setOpenInfoDialogue,
    handleCloseInfoDialogue,
    openInfoDialogue,
    infoMsgTitle,
    infoMsgLabel,
    infoMsgContent,
    showErrorMessage
  } = useContext(UIContext);

  const navigate = useNavigate();
  const location = useLocation();
  const [, vectorFocus] = useGetVectorFocus({ id: location.state?.vectorFocusId });
  const [, denounceStatus] = useGetDenounceStatusForSelector(true);
  const [, possibleVectorFocus] = useGetPossibleVectorFocusForSelector(true);
  const [, diseaseVectors] = useGetDiseaseVectorsForSelector(true);

  const [selectedImageFile, setSelectedFile] = useState(null);
  const [selectedImageFileURI, setSelectedImageFileURI] = useState(null);
  const [wantUpdateImage, setWantUpdateImage] = useState(false);

  useEffect(() => {
    if (!isAuthorized) navigate('/login');
  }, [isAuthorized, navigate]);

  function handleClickOnUpdateImgBtn() {
    setSelectedFile(null);
    setWantUpdateImage(true);
  }

  function handleOnImageCapture(imageData) {
    setSelectedImageFileURI(imageData);
    setWantUpdateImage(false);
  }

  async function handleSubmitVectorFocusForm(values, { setSubmitting }) {
    let updatedPicture = null;

    if (selectedImageFile) {
      const pictureService = new PicturesService();
      const resOpPicture = await pictureService.upsertPicture({
        pictureId: vectorFocus.picture?.id,
        pickedImgFile: selectedImageFile,
        currentUserId: authUser?.user?.id
      });
      updatedPicture = resOpPicture?.data;

      if (resOpPicture.status !== 1 || !updatedPicture) {
        showErrorMessage({
          message: "Houve uma falha ao enviar a foto"
        });
        setSubmitting(false);
        return;
      }
    }

    const vectorFocusService = new VectorFocusService();
    const resUpdateFocus = await vectorFocusService.updateVectorFocus({
      vectorFocusId: vectorFocus.id,
      diseaseVectorId: values.diseaseVector.id,
      denounceStatusId: values.denounceStatus.id,
      description: values.description,
      pictureId: updatedPicture ? updatedPicture.id : vectorFocus.picture?.id,
      possibleFocusId: values.possibleFocus.id
    });

    if (resUpdateFocus.status !== 1) {
      showErrorMessage({
        message: "Não foi possível atualizer o foco"
      });
      setSubmitting(false);
      return;
    }

    setOpenInfoDialogue({
      open: true,
      msgId: INFO_DIALOGUE_MSGS.VECTOR_FOCUS_HAS_BEEN_UPDATED
    });
    setSubmitting(false);
  }

  async function handleSubmitAddressForm(values, { setSubmitting }) {
    const addressService = new AddressService();
    const repUpsertAddress = await addressService.upsertAddress({
      countryId: vectorFocus?.city?.country_id,
      stateId: vectorFocus?.city?.state_id,
      cityId: vectorFocus?.city?.id,
      neighborhood: values.neighborhood,
      street: values.street,
      number: values.number,
      zipCode: values.zipCode,
      additionalInfo: values.complement
    });
    const address = repUpsertAddress?.data;

    if (repUpsertAddress.status !== 1 || !address) {
      showErrorMessage({
        message: "Não foi possível cadastrar/atualizar o endereço"
      });
      setSubmitting(false);
      return;
    }

    const vectorFocusService = new VectorFocusService();
    const resUpdateFocus = await vectorFocusService.updateVectorFocus({
      vectorFocusId: vectorFocus.id,
      pictureId: vectorFocus.picture_id,
      addressId: address.id,
      diseaseVectorId: vectorFocus.disease_vector_id,
      denounceStatusId: vectorFocus.denounce_status_id,
      description: vectorFocus.description,
      possibleFocusId: vectorFocus.possible_focus_id
    });

    if (resUpdateFocus.status !== 1) {
      showErrorMessage({
        message: "Não foi possível atualizar o novo foco com o endereço"
      });
      setSubmitting(false);
      return;
    }

    setOpenInfoDialogue({
      open: true,
      msgId: INFO_DIALOGUE_MSGS.VECTOR_FOCUS_ADDRESS_HAS_BEEN_UPDATED,
      callback: () => navigate(-1)
    });
    setSubmitting(false);
  }

  function renderVectorFocusImage() {
    let vectorImage = null;
    const imageUrl = selectedImageFileURI || vectorFocus?.picture?.url;

    if (wantUpdateImage) {
      vectorImage = <CameraCapture
        setSelectedFile={setSelectedFile}
        onImageCapture={handleOnImageCapture}
        className="vector-focus-image" />;
    } else {
      vectorImage = (
        <div>
          {imageUrl && <Image src={imageUrl} className="vector-focus-image" />}
          <Button className="update-vector-focus-image-btn" onClick={handleClickOnUpdateImgBtn}>
            <CameraAltIcon />
          </Button>
        </div>
      );
    }

    return (
      <div className="header-image">
        <Paper className="vector-focus-image-container">
          {vectorImage}
        </Paper>
      </div>
    );
  }

  const imageContainer = renderVectorFocusImage();
  const vectorFocusAddress = {
    city: vectorFocus?.city?.name,
    neighborhood: vectorFocus?.address?.neighborhood,
    street: vectorFocus?.address?.street,
    number: vectorFocus?.address?.number,
    complement: vectorFocus?.address?.additional_info,
    zipCode: vectorFocus?.address?.zip_code
  };

  return (
    <div className="inner-page">
      <Header
        showBackBtn
        hideBackBtnLabel
        pageTitle="Atualizar Foco"
      />
      <div className="inner-page-content-container">
        {imageContainer}
        <UpdateVectorFocusForm
          vectorFocus={vectorFocus}
          denounceStatus={denounceStatus}
          diseaseVectors={diseaseVectors}
          possibleVectorFocus={possibleVectorFocus}
          onSubmit={handleSubmitVectorFocusForm} />
        <p className="section-title">Confirme o endereço</p>
        {vectorFocus && (
          <AddressForm
            onSubmit={handleSubmitAddressForm}
            city={vectorFocusAddress.city}
            neighborhood={vectorFocusAddress.neighborhood}
            street={vectorFocusAddress.street}
            number={vectorFocusAddress.number}
            complement={vectorFocusAddress.complement}
            zipCode={vectorFocusAddress.zipCode} />
        )}
      </div>
      <InformationDialogue
        title={infoMsgTitle}
        confirmLabel={infoMsgLabel}
        open={openInfoDialogue}
        onClose={handleCloseInfoDialogue}
      >
        {infoMsgContent}
      </InformationDialogue>
      <BottomMenu />
    </div>
  );
}

export default VectorFocusUpdate;
